// Mock data for BrainWave Technologies website

export const company = {
  name: "BrainWave Technologies",
  tagline: "Thinking out of the box.",
  description: "Professional IT Consulting and Project Management for enterprise-grade IBM Power Systems and AIX environments.",
  founded: 2013,
  phone: "+27 84 4444 915",
  email: "info@brainwave-group.com",
  address: "Sandton, Johannesburg, South Africa",
  hours: "Mon–Fri • 08:00 – 17:00 SAST"
};

export const navLinks = [
  { label: "Home", to: "/" },
  { label: "About", to: "/about" },
  { label: "Services", to: "/services" },
  { label: "Support", to: "/support" },
  { label: "News", to: "/news" },
  { label: "Contact", to: "/contact" }
];

export const services = [
  {
    id: "vio-powervm",
    title: "VIO (PowerVM)",
    short: "Enterprise virtualization built on IBM PowerVM.",
    description:
      "Detailed system planning, installation and customization of VIO servers, micro-partitioning, shared Ethernet adapters, NPIV, live partition mobility, and storage virtualization across your entire Power estate.",
    bullets: [
      "Detailed planning & sizing",
      "Dual-VIOS installation & customization",
      "Micro-partitioning & LPAR design",
      "Live Partition Mobility (LPM)",
      "NPIV, SEA & VSCSI configuration",
      "Knowledge transfer & documentation"
    ],
    icon: "Server"
  },
  {
    id: "hacmp-powerha",
    title: "HACMP (PowerHA)",
    short: "High availability clustering for mission-critical AIX workloads.",
    description:
      "Design, deployment and support of PowerHA SystemMirror clusters. We keep your databases, ERP and line-of-business apps resilient to node, site and storage failure.",
    bullets: [
      "Two to eight node clusters",
      "Stretched & linked cluster topologies",
      "Oracle, DB2 & SAP integration",
      "GLVM & cross-site replication",
      "DR runbooks & failover testing",
      "24/7 remote cluster monitoring"
    ],
    icon: "ShieldCheck"
  },
  {
    id: "lvm",
    title: "AIX LVM & Storage",
    short: "Rock-solid storage architecture on AIX Logical Volume Manager.",
    description:
      "Volume group design, mirrored rootvg recovery, JFS2 tuning and SAN migration services. From bad rootvg disks to full datacenter migrations, we've seen and solved it.",
    bullets: [
      "VG, LV & FS design",
      "Rootvg recovery & cloning (alt_disk)",
      "SAN migration (EMC, IBM, Hitachi)",
      "JFS2 performance tuning",
      "Mirrorvg & splitvg strategies",
      "Capacity planning"
    ],
    icon: "HardDrive"
  },
  {
    id: "aix-admin",
    title: "AIX Systems Administration",
    short: "Onsite and remote AIX administration — from 4.3.3 through 7.3.",
    description:
      "Day-to-day AIX systems administration, change management, patching, performance tuning and break-fix support — delivered by engineers with 15+ years on the platform.",
    bullets: [
      "AIX 4.3.3 through 7.3 support",
      "TL/SP upgrades & patching",
      "Performance & tuning (nmon/topas)",
      "ITIL-aligned change management",
      "Security hardening",
      "Scripting & automation"
    ],
    icon: "Terminal"
  },
  {
    id: "migrations",
    title: "Power Migrations & Upgrades",
    short: "Smooth migrations between Power6, 7, 8, 9 and Power10.",
    description:
      "End-to-end migration projects — hardware refresh, OS upgrades, and workload consolidation with minimum downtime and a fully documented fallback plan.",
    bullets: [
      "Power6/7 → Power9/10 upgrades",
      "AIX OS level migrations",
      "LPM-based zero-downtime moves",
      "Backup & DR validation",
      "Cutover & rollback planning",
      "Post-migration optimisation"
    ],
    icon: "ArrowUpRight"
  },
  {
    id: "training",
    title: "Training & Knowledge Transfer",
    short: "Formal AIX, HACMP, LVM and VIO training — onsite or remote.",
    description:
      "Custom-built courses for your team. Classroom-style lectures combined with hands-on lab time on real Power LPARs provisioned for each delegate.",
    bullets: [
      "AIX fundamentals & advanced",
      "HACMP / PowerHA cluster admin",
      "VIO server deep-dive",
      "LVM workshops",
      "Hands-on LPAR labs",
      "Certificates of completion"
    ],
    icon: "GraduationCap"
  }
];

export const stats = [
  { value: "15+", label: "Years on IBM Power" },
  { value: "200+", label: "LPARs under management" },
  { value: "99.99%", label: "Cluster uptime delivered" },
  { value: "24/7", label: "Remote support cover" }
];

export const clients = [
  { name: "IBM", logo: "https://upload.wikimedia.org/wikipedia/commons/thumb/5/51/IBM_logo.svg/1200px-IBM_logo.svg.png" },
  { name: "SARS", logo: "https://upload.wikimedia.org/wikipedia/commons/thumb/c/ca/SARS_new_Logo.png/1200px-SARS_new_Logo.png" },
  { name: "Transnet", logo: "https://upload.wikimedia.org/wikipedia/commons/thumb/c/c5/Transnet_logo.svg/2560px-Transnet_logo.svg.png" },
  { name: "Standard Bank", logo: "https://upload.wikimedia.org/wikipedia/commons/thumb/4/4f/Standard_Bank_Group_Logo.svg/2560px-Standard_Bank_Group_Logo.svg.png" },
  { name: "Absa", logo: "https://upload.wikimedia.org/wikipedia/commons/thumb/1/18/Absa_Logo.svg/2560px-Absa_Logo.svg.png" },
  { name: "MTN", logo: "https://upload.wikimedia.org/wikipedia/commons/thumb/4/4e/MTN_Logo.svg/2560px-MTN_Logo.svg.png" }
];

export const testimonials = [
  {
    id: 1,
    quote:
      "BrainWave delivered our PowerHA cluster migration without a single minute of unplanned downtime. Their runbooks are the cleanest I have ever reviewed.",
    name: "Thabo Mokoena",
    role: "Head of Infrastructure, Financial Services"
  },
  {
    id: 2,
    quote:
      "We moved from Power7 to Power10 over a weekend. The BrainWave team planned every LPAR, every WWPN, every fallback step. Flawless.",
    name: "Priya Naidoo",
    role: "IT Operations Manager"
  },
  {
    id: 3,
    quote:
      "Deep AIX expertise is rare. These engineers know the stack cold — from LVM internals to PowerVM micro-partitioning.",
    name: "Johan van der Merwe",
    role: "Enterprise Architect"
  }
];

export const process = [
  {
    step: "01",
    title: "Discover",
    text: "We start by listening. Workshops with your stakeholders, a full audit of the existing estate, and a clear picture of where you want to go."
  },
  {
    step: "02",
    title: "Design",
    text: "A detailed low-level design document — LPAR layout, storage zoning, network, HA topology — signed off before a single change is made."
  },
  {
    step: "03",
    title: "Deploy",
    text: "Change-managed, tested implementation. Every step is documented, reversible, and validated against the success criteria."
  },
  {
    step: "04",
    title: "Support",
    text: "24/7 managed support with proactive monitoring, quarterly health checks and a named technical account manager."
  }
];

export const news = [
  {
    id: "power11-ready",
    title: "Getting your AIX estate ready for Power11",
    category: "Infrastructure",
    date: "Jun 18, 2025",
    readTime: "6 min read",
    image: "https://images.unsplash.com/photo-1639066648921-82d4500abf1a?crop=entropy&cs=srgb&fm=jpg&q=85",
    excerpt:
      "IBM's Power11 announcement changes the hardware refresh calculus. Here's a checklist to validate AIX versions, firmware and HMC prerequisites before you order."
  },
  {
    id: "powerha-patterns",
    title: "Five PowerHA patterns we still see break in production",
    category: "High Availability",
    date: "May 29, 2025",
    readTime: "8 min read",
    image: "https://images.pexels.com/photos/4508751/pexels-photo-4508751.jpeg?auto=compress&cs=tinysrgb&dpr=2&h=650&w=940",
    excerpt:
      "From mis-configured heartbeat networks to forgotten application monitors — the common HACMP mistakes that still cause outages in 2025."
  },
  {
    id: "vios-shared-storage",
    title: "Dual-VIOS with shared storage pools — a practical guide",
    category: "Virtualization",
    date: "May 04, 2025",
    readTime: "10 min read",
    image: "https://images.pexels.com/photos/4682189/pexels-photo-4682189.jpeg?auto=compress&cs=tinysrgb&dpr=2&h=650&w=940",
    excerpt:
      "A working example of a dual-VIOS design using shared storage pools, NPIV and SEA failover with load sharing — config snippets included."
  },
  {
    id: "lvm-rootvg-recovery",
    title: "Recovering a corrupt rootvg without downtime",
    category: "AIX",
    date: "Apr 12, 2025",
    readTime: "5 min read",
    image: "https://images.pexels.com/photos/5480781/pexels-photo-5480781.jpeg?auto=compress&cs=tinysrgb&dpr=2&h=650&w=940",
    excerpt:
      "alt_disk_copy, rootvg mirroring and a tested recovery script — the three tools you want in place before a bad disk bites you."
  },
  {
    id: "green-data-centre",
    title: "Greener Power estates: consolidation done right",
    category: "Green IT",
    date: "Mar 28, 2025",
    readTime: "4 min read",
    image: "https://images.unsplash.com/photo-1558494949-ef010cbdcc31?crop=entropy&cs=srgb&fm=jpg&q=85",
    excerpt:
      "How we cut one client's Power estate from 14 frames to 4 — saving 38% on power and cooling without sacrificing headroom."
  },
  {
    id: "nmon-primer",
    title: "A modern primer on nmon for AIX performance tuning",
    category: "Performance",
    date: "Feb 20, 2025",
    readTime: "7 min read",
    image: "https://images.pexels.com/photos/5092815/pexels-photo-5092815.jpeg?auto=compress&cs=tinysrgb&dpr=2&h=650&w=940",
    excerpt:
      "Everything nmon can still tell you that dashboards miss — with real captures from an Oracle RAC workload on Power9."
  }
];

export const values = [
  {
    title: "Deep Expertise",
    text: "Our engineers average 15 years on the IBM Power platform. AIX, PowerVM, PowerHA — they've lived every corner of it."
  },
  {
    title: "Zero-drama Delivery",
    text: "Detailed planning, peer review and change-managed execution. No heroics — just predictable, boring, successful cutovers."
  },
  {
    title: "Clear Communication",
    text: "Weekly status, a dedicated Slack/Teams channel and plain-English documentation. Your team stays in the loop."
  },
  {
    title: "Knowledge Transfer",
    text: "We don't hide behind complexity. At the end of every engagement your team can run what we built."
  }
];

export const supportPlans = [
  {
    name: "Essential",
    price: "From R14,900",
    cycle: "/ month",
    description: "Business-hours support for small Power estates.",
    features: [
      "Business hours (08:00–17:00 SAST)",
      "Up to 10 LPARs",
      "4-hour response SLA",
      "Monthly health check",
      "Email & phone support"
    ],
    highlight: false
  },
  {
    name: "Professional",
    price: "From R29,900",
    cycle: "/ month",
    description: "Extended hours with proactive monitoring.",
    features: [
      "Extended hours (06:00–22:00)",
      "Up to 40 LPARs",
      "1-hour response SLA",
      "Proactive nmon monitoring",
      "Quarterly cluster DR test",
      "Named technical lead"
    ],
    highlight: true
  },
  {
    name: "Enterprise 24×7",
    price: "Custom",
    cycle: "",
    description: "Round-the-clock coverage for mission-critical workloads.",
    features: [
      "24/7/365 coverage",
      "Unlimited LPARs",
      "15-minute response SLA",
      "On-call cluster engineer",
      "Quarterly architecture review",
      "Dedicated TAM"
    ],
    highlight: false
  }
];

export const faqs = [
  {
    q: "Which versions of AIX do you support?",
    a: "Everything from AIX 4.3.3 through AIX 7.3. We still actively support customers running legacy workloads on 5.3 and 6.1 while planning their upgrade path."
  },
  {
    q: "Can you work with our existing IBM support contract?",
    a: "Absolutely. We complement your IBM hardware/software support, handling the day-to-day administration, project work and escalations to IBM when required."
  },
  {
    q: "Do you offer remote-only engagements?",
    a: "Yes. Most of our work is delivered remotely over secure VPN. We mobilise onsite for critical cutovers or when customer policy requires it."
  },
  {
    q: "What regions do you cover?",
    a: "Head office in Johannesburg, with active engagements across Southern Africa, the Middle East and selected European clients."
  },
  {
    q: "How quickly can you start?",
    a: "For break-fix support we can engage within hours. Project engagements typically kick off within 1–2 weeks of contract signature."
  }
];

export const images = {
  hero: "https://images.unsplash.com/photo-1580106815433-a5b1d1d53d85?crop=entropy&cs=srgb&fm=jpg&q=85",
  dataCenter: "https://images.unsplash.com/photo-1558494949-ef010cbdcc31?crop=entropy&cs=srgb&fm=jpg&q=85",
  serversRack: "https://images.pexels.com/photos/4508751/pexels-photo-4508751.jpeg?auto=compress&cs=tinysrgb&dpr=2&h=650&w=940",
  serverArray: "https://images.unsplash.com/photo-1639066648921-82d4500abf1a?crop=entropy&cs=srgb&fm=jpg&q=85",
  rackMounted: "https://images.pexels.com/photos/17489152/pexels-photo-17489152.jpeg?auto=compress&cs=tinysrgb&dpr=2&h=650&w=940",
  serverMgmt: "https://images.pexels.com/photos/5480781/pexels-photo-5480781.jpeg?auto=compress&cs=tinysrgb&dpr=2&h=650&w=940",
  cables: "https://images.pexels.com/photos/4682189/pexels-photo-4682189.jpeg?auto=compress&cs=tinysrgb&dpr=2&h=650&w=940",
  monitor: "https://images.pexels.com/photos/5092815/pexels-photo-5092815.jpeg?auto=compress&cs=tinysrgb&dpr=2&h=650&w=940"
};
